import React from 'react';
import AbcIcon from '@mui/icons-material/Abc';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import CalculateIcon from '@mui/icons-material/Calculate';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DesignIcon from '@mui/icons-material/DesignServices';
import EditIcon from '@mui/icons-material/Edit';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PreviewIcon from '@mui/icons-material/Preview';
import Refresh from '@mui/icons-material/Refresh';
import RoundedCornerIcon from '@mui/icons-material/RoundedCorner';
import StarsIcon from '@mui/icons-material/Stars';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { Divider } from '@mui/material';

import { SelectedItemIdentifier } from '../../../../library/selection/types';
import { TransformationMenuItems } from '../../../../library/transformations/controls/TransformationMenuItems';
import { useAppMonitor } from '../../../../monitor';
import {
  beginRenamingDesignsAssociatedWithTrussEnvelopes,
  designSelectedTrussEnvelopes,
  designTrussEnvelope,
  flipTrussEnvelopes,
  recalculateExistingTrussEnvelopes,
  regenerateComponentsInEnvelopes,
  regenerateConnectionsOnEnvelopes,
  saveTrussEnvelopes,
  startInsertingCornerSet,
  startInsertingTrussEnvelopeRun,
  startTrimOrExtendEnvelopes,
} from '../../../../store/actions/layout/trussEnvelopeActions';
import { showSelectComponentDesignDialog } from '../../../../store/actions/popupActions';
import {
  handleStandardSceneItemEditPropertiesAction,
  handleStandardSceneItemsEditPropertiesAction,
} from '../../../../store/actions/sceneInteractionActions';
import { selectItem } from '../../../../store/actions/selectActions';
import { instantInsertTrussGroup } from '../../../../store/actions/trussGroups';
import { isLayoutBasedOnRoofContainers } from '../../../../store/selectors/layoutModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import CascadingSceneMenuItem from '../common/CascadingSceneMenuItem';
import { SceneMenuContent } from '../common/SceneMenuContent';
import { SceneMenuFragment } from '../common/SceneMenuFragment';
import { SceneMenuItem } from '../common/SceneMenuItem';
import { DeleteMenuItem } from '../shared-menu-items/DeleteMenuItem';

export function TrussEnvelopeMenu(props: { guid: string }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const openContext = useAppSelector((state) => state.application.openContext);

  const componentDesigns = useAppSelector((state) => state.componentDesigns.data);
  const trussEnvelope = useAppSelector((state) => state.layout.trussEnvelopes[props.guid]);

  const isRoofContainerLayout = useAppSelector(isLayoutBasedOnRoofContainers);

  if (trussEnvelope == null) throw new Error('Unable to find truss envelope with active menu.');

  const componentDesign =
    trussEnvelope.componentDesignGuid != null ? componentDesigns[trussEnvelope.componentDesignGuid] : null;

  return (
    <SceneMenuContent>
      <SceneMenuItem
        text="Edit Properties"
        icon={<EditIcon />}
        onClick={async () => {
          await dispatch(handleStandardSceneItemEditPropertiesAction('trussEnvelope', trussEnvelope));
          monitor.recordInteraction('Truss Envelope => Edit Properties menu item clicked');
        }}
      />

      {openContext?.type === 'project' && (
        <SceneMenuItem
          text="Create Truss Group"
          icon={<AutoAwesomeMotionIcon />}
          onClick={() => {
            dispatch(instantInsertTrussGroup([trussEnvelope.guid]));
            monitor.recordInteraction('Truss Envelope => Create Truss Group menu item clicked');
          }}
        />
      )}

      <TransformationMenuItems />

      {trussEnvelope.componentDesignGuid != null && (
        <SceneMenuItem
          text="Rename Component"
          icon={<AbcIcon />}
          onClick={() => {
            dispatch(beginRenamingDesignsAssociatedWithTrussEnvelopes([trussEnvelope]));
            monitor.recordInteraction('Truss Envelope => Rename Component menu item clicked');
          }}
        />
      )}

      <Divider />

      <SceneMenuItem
        text="Trim/Extend"
        icon={<CompareArrowsIcon />}
        onClick={async () => {
          dispatch(startTrimOrExtendEnvelopes());
          monitor.recordInteraction('Truss Envelope => Trim/Extend menu item clicked');
        }}
      />

      <SceneMenuItem
        text="Flip Truss Envelope"
        icon={<SwapVertIcon />}
        onClick={async () => {
          dispatch(flipTrussEnvelopes(trussEnvelope));
          monitor.recordInteraction('Truss Envelope => Flip Envelope menu item clicked');
        }}
      />

      <SceneMenuItem
        text="Recalculate Now"
        icon={<CalculateIcon />}
        onClick={async () => {
          await dispatch(recalculateExistingTrussEnvelopes(trussEnvelope));
          monitor.recordInteraction('Truss Envelope => Recalculate Now menu item clicked');
        }}
      />

      <CascadingSceneMenuItem label="Partially Recalculate" icon={<CalculateOutlinedIcon />}>
        <SceneMenuItem
          text="Recalculate Left End Only"
          onClick={async () => {
            await dispatch(recalculateExistingTrussEnvelopes(trussEnvelope, { end: 'left' }));
            monitor.recordInteraction(
              'Truss Envelope => Partially Recalculate => Recalculate Left End Only menu item clicked',
            );
          }}
        />

        <SceneMenuItem
          text="Recalculate Right End Only"
          onClick={async () => {
            await dispatch(recalculateExistingTrussEnvelopes(trussEnvelope, { end: 'right' }));
            monitor.recordInteraction(
              'Truss Envelope => Partially Recalculate => Recalculate Right End Only menu item clicked',
            );
          }}
        />
      </CascadingSceneMenuItem>

      {trussEnvelope.componentDesignGuid != null && (
        <SceneMenuItem
          text="Regenerate Component"
          icon={<Refresh />}
          onClick={async () => {
            await dispatch(regenerateComponentsInEnvelopes([trussEnvelope]));
            monitor.recordInteraction('Truss Envelopes => Regenerate Component menu item clicked');
          }}
        />
      )}

      <SceneMenuItem
        text="Regenerate Connections"
        icon={<Refresh />}
        onClick={async () => {
          await dispatch(regenerateConnectionsOnEnvelopes([trussEnvelope]));
          monitor.recordInteraction('Truss Envelope => Regenerate Connections menu item clicked');
        }}
      />

      <Divider />

      <SceneMenuItem
        text="Start Run"
        icon={<FormatLineSpacingIcon />}
        onClick={async () => {
          dispatch(startInsertingTrussEnvelopeRun(trussEnvelope.guid));

          monitor.recordInteraction('Truss Envelope => Start Run menu item clicked');
        }}
      />

      {/** Roof container layouts are not yet supported by the corner set tool. */}
      {!isRoofContainerLayout && (
        <CascadingSceneMenuItem
          label="Create Corner Set"
          icon={<RoundedCornerIcon />}
          onClick={() => {
            dispatch(startInsertingCornerSet(trussEnvelope.guid, 'Stepdown'));
            monitor.recordInteraction('Truss Envelope => Create Corner Set menu item clicked');
          }}
        >
          <SceneMenuItem
            text="Stepdown"
            onClick={(event) => {
              event.stopPropagation();
              dispatch(startInsertingCornerSet(trussEnvelope.guid, 'Stepdown'));
              monitor.recordInteraction('Truss Envelope => Create Corner Set => Stepdown menu item clicked');
            }}
          />

          <SceneMenuItem
            text="Atlantic"
            onClick={(event) => {
              event.stopPropagation();
              dispatch(startInsertingCornerSet(trussEnvelope.guid, 'Atlantic'));
              monitor.recordInteraction('Truss Envelope => Create Corner Set => Atlantic menu item clicked');
            }}
          />
        </CascadingSceneMenuItem>
      )}

      <Divider />

      {openContext?.type === 'project' && trussEnvelope.componentDesignGuid == null && (
        <SceneMenuFragment>
          <SceneMenuItem
            text="Create or Match Design"
            icon={<DesignIcon />}
            onClick={async () => {
              await dispatch(
                designTrussEnvelope(trussEnvelope, { matchExistingDesigns: true, jumpToTrussInDesignView: false }),
              );
              monitor.recordInteraction('Truss Envelope => Create or Match Design menu item clicked');
            }}
          />

          <SceneMenuItem
            text="Create New Design"
            icon={<StarsIcon />}
            onClick={async () => {
              await dispatch(
                designTrussEnvelope(trussEnvelope, { matchExistingDesigns: false, jumpToTrussInDesignView: false }),
              );
              monitor.recordInteraction('Truss Envelope => Create New Design menu item clicked');
            }}
          />
        </SceneMenuFragment>
      )}

      {componentDesign && (
        <SceneMenuItem
          text="View Component"
          icon={<PreviewIcon />}
          onClick={() => {
            dispatch(selectItem(componentDesign, 'componentDesign'));
            monitor.recordInteraction('Truss Envelope => View Component Design menu item clicked');
          }}
        />
      )}

      {trussEnvelope.componentDesignGuid ? (
        <SceneMenuItem
          text="Unlink Component"
          icon={<LinkOffIcon />}
          onClick={() => {
            dispatch(saveTrussEnvelopes({ ...trussEnvelope, componentDesignGuid: null }));
            monitor.recordInteraction('Truss Envelope => Unlink Component menu item clicked');
          }}
        />
      ) : (
        <SceneMenuItem
          text="Choose Existing Design"
          icon={<LinkIcon />}
          onClick={() => {
            dispatch(
              showSelectComponentDesignDialog({
                continuation: { type: 'associate-with-envelope', trussEnvelopeGuids: [trussEnvelope.guid] },
              }),
            );

            monitor.recordInteraction('Truss Envelope => Choose Existing Design menu item clicked');
          }}
        />
      )}

      <Divider />

      <DeleteMenuItem
        type="trussEnvelope"
        item={trussEnvelope}
        userVisibleTerm="Envelope"
        onBeforeDelete={(proceed) => {
          if (confirm('Are you sure you want to delete this truss envelope?')) {
            proceed();
          } else {
            monitor.recordInteraction('Truss Envelope => Delete Envelope menu item clicked, but cancelled');
          }
        }}
      />
    </SceneMenuContent>
  );
}

export function BulkTrussEnvelopeMenu(props: { items: SelectedItemIdentifier[] }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const trussEnvelopeStore = useAppSelector((state) => state.layout.trussEnvelopes);
  const trussEnvelopes = props.items.map((item) => trussEnvelopeStore[item.guid]);

  const inProjectContext = useAppSelector((state) => state.application.openContext?.type === 'project');

  const designedEnvelopes = trussEnvelopes.filter((envelope) => envelope.componentDesignGuid != null);

  const noEnvelopesDesigned = designedEnvelopes.length === 0;
  const allEnvelopesDesigned = designedEnvelopes.length === trussEnvelopes.length;

  return (
    <SceneMenuContent>
      <SceneMenuItem
        text="Edit Properties"
        icon={<EditIcon />}
        onClick={() => {
          dispatch(handleStandardSceneItemsEditPropertiesAction('trussEnvelope', trussEnvelopes));
          monitor.recordInteraction('Truss Envelopes => Edit Properties menu item clicked');
        }}
      />

      {inProjectContext && (
        <SceneMenuItem
          text="Create Truss Group"
          icon={<AutoAwesomeMotionIcon />}
          onClick={() => {
            dispatch(instantInsertTrussGroup(trussEnvelopes.map((envelope) => envelope.guid)));
            monitor.recordInteraction('Truss Envelopes => Create Truss Group menu item clicked');
          }}
        />
      )}

      <TransformationMenuItems />

      {!noEnvelopesDesigned && (
        <SceneMenuItem
          text="Rename Components"
          icon={<AbcIcon />}
          onClick={() => {
            dispatch(beginRenamingDesignsAssociatedWithTrussEnvelopes(trussEnvelopes));
            monitor.recordInteraction('Truss Envelopes => Rename Components menu item clicked');
          }}
        />
      )}

      <Divider />

      <SceneMenuItem
        text="Trim/Extend"
        icon={<CompareArrowsIcon />}
        onClick={async () => {
          dispatch(startTrimOrExtendEnvelopes());
          monitor.recordInteraction('Truss Envelopes => Trim/Extend menu item clicked');
        }}
      />

      <SceneMenuItem
        text="Flip Truss Envelopes"
        icon={<SwapVertIcon />}
        onClick={async () => {
          dispatch(flipTrussEnvelopes(trussEnvelopes));
          monitor.recordInteraction('Truss Envelopes => Flip Envelopes menu item clicked');
        }}
      />

      <SceneMenuItem
        text="Recalculate Now"
        icon={<CalculateIcon />}
        onClick={async () => {
          await dispatch(recalculateExistingTrussEnvelopes(trussEnvelopes));
          monitor.recordInteraction('Truss Envelopes => Recalculate Now menu item clicked');
        }}
      />

      <CascadingSceneMenuItem label="Partially Recalculate" icon={<CalculateOutlinedIcon />}>
        <SceneMenuItem
          text="Recalculate Left End Only"
          onClick={async () => {
            await dispatch(recalculateExistingTrussEnvelopes(trussEnvelopes, { end: 'left' }));
            monitor.recordInteraction(
              'Truss Envelopes => Partially Recalculate => Recalculate Left End Only menu item clicked',
            );
          }}
        />

        <SceneMenuItem
          text="Recalculate Right End Only"
          onClick={async () => {
            await dispatch(recalculateExistingTrussEnvelopes(trussEnvelopes, { end: 'right' }));
            monitor.recordInteraction(
              'Truss Envelopes => Partially Recalculate => Recalculate Right End Only menu item clicked',
            );
          }}
        />
      </CascadingSceneMenuItem>

      {allEnvelopesDesigned && (
        <SceneMenuItem
          text="Regenerate Components"
          icon={<Refresh />}
          onClick={async () => {
            await dispatch(regenerateComponentsInEnvelopes(trussEnvelopes));
            monitor.recordInteraction('Truss Envelopes => Regenerate Components menu item clicked');
          }}
        />
      )}

      <SceneMenuItem
        text="Regenerate Connections"
        icon={<Refresh />}
        onClick={async () => {
          await dispatch(regenerateConnectionsOnEnvelopes(trussEnvelopes));
          monitor.recordInteraction('Truss Envelopes => Regenerate Connections menu item clicked');
        }}
      />

      <Divider />

      {allEnvelopesDesigned && (
        <SceneMenuItem
          text="Unlink Components"
          icon={<LinkOffIcon />}
          onClick={() => {
            dispatch(
              saveTrussEnvelopes(designedEnvelopes.map((envelope) => ({ ...envelope, componentDesignGuid: null }))),
            );
            monitor.recordInteraction('Truss Envelopes => Unlink Components menu item clicked');
          }}
        />
      )}

      {noEnvelopesDesigned && (
        <SceneMenuItem
          text="Create or Match Designs"
          icon={<DesignIcon />}
          onClick={async () => {
            await dispatch(designSelectedTrussEnvelopes());
            monitor.recordInteraction('Truss Envelopes => Create or Match Designs menu item clicked');
          }}
        />
      )}

      {noEnvelopesDesigned && (
        <SceneMenuItem
          text="Choose Existing Design"
          icon={<LinkIcon />}
          onClick={() => {
            dispatch(
              showSelectComponentDesignDialog({
                continuation: {
                  type: 'associate-with-envelope',
                  trussEnvelopeGuids: trussEnvelopes.map((envelope) => envelope.guid),
                },
              }),
            );

            monitor.recordInteraction('Truss Envelopes => Choose Existing Design menu item clicked');
          }}
        />
      )}

      {(allEnvelopesDesigned || noEnvelopesDesigned) && <Divider />}

      <DeleteMenuItem
        type="trussEnvelope"
        item={props.items}
        userVisibleTerm="Envelopes"
        onBeforeDelete={(proceed) => {
          if (confirm('Are you sure you want to delete these truss envelopes?')) {
            proceed();
          } else {
            monitor.recordInteraction('Truss Envelopes => Delete Envelopes menu item clicked, but cancelled');
          }
        }}
      />
    </SceneMenuContent>
  );
}
